import React, { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import API, { getApiResourceUrl } from '../services/api';
import { getAppPath } from '../utils/runtimeMode';

const NewBusinessSection = ({ 
  newBusinessSections, 
  setNewBusinessSections, 
  committeeMembers,
  addCorrectiveAction,
  onAttachmentsChange,
  newBusinessData,
  setNewBusinessData,
  initialAttachments = [],
  readOnly = false
}) => {
  // Inspection reports pulled from VehicleInspection & InspectionApp
  const [inspectionOptions, setInspectionOptions] = useState([]);
  const [inspectionLoading, setInspectionLoading] = useState(false);
  const [inspectionError, setInspectionError] = useState(null);
  const [selectedInspectionIds, setSelectedInspectionIds] = useState([]);
  // Incident & investigation reports for committee minutes
  const [incidentOptions, setIncidentOptions] = useState([]);
  const [incidentLoading, setIncidentLoading] = useState(false);
  const [incidentError, setIncidentError] = useState(null);
  const [selectedIncidentIds, setSelectedIncidentIds] = useState([]);
  const [selectedIncidentDetails, setSelectedIncidentDetails] = useState({});
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [attachmentError, setAttachmentError] = useState(null);
  const [uploadedAttachments, setUploadedAttachments] = useState([]);

  // Sync uploadedAttachments with initialAttachments from parent (for draft/finalized restore)
  useEffect(() => {
    if (initialAttachments && initialAttachments.length > 0) {
      setUploadedAttachments(initialAttachments);
    }
  }, [initialAttachments]);

  useEffect(() => {
    if (!newBusinessSections.inspectionReports) return;
    let cancelled = false;
    async function loadInspections(){
      setInspectionLoading(true); setInspectionError(null);
      try {
        // Pull from InspectionApp; could be extended to also merge vehicle inspections later
        const res = await API.get('/api/inspectionReports/committee/list');
        if (!cancelled) {
          if (res.data && res.data.success) {
            setInspectionOptions(res.data.reports || []);
          } else if (Array.isArray(res.data)) {
            // fallback if endpoint returns raw rows
            setInspectionOptions(res.data);
          } else {
            throw new Error(res.data?.error || 'Failed to load inspection reports');
          }
        }
      } catch (e) {
        if (!cancelled) setInspectionError(e.message || 'Failed to load inspection reports');
      } finally {
        if (!cancelled) setInspectionLoading(false);
      }
    }
    loadInspections();
    return () => { cancelled = true; };
  }, [newBusinessSections.inspectionReports]);

  // Load incident + investigation reports when that section is enabled
  useEffect(() => {
    if (!newBusinessSections.investigations) return;
    let cancelled = false;
    async function loadIncidents() {
      setIncidentLoading(true); setIncidentError(null);
      try {
        const res = await API.get('/api/incidents/committee/list');
        if (!cancelled) {
          if (res.data && res.data.success && Array.isArray(res.data.incidents)) {
            setIncidentOptions(res.data.incidents);
          } else if (Array.isArray(res.data)) {
            setIncidentOptions(res.data);
          } else {
            throw new Error(res.data?.message || 'Failed to load incident reports');
          }
        }
      } catch (e) {
        if (!cancelled) setIncidentError(e.message || 'Failed to load incident reports');
      } finally {
        if (!cancelled) setIncidentLoading(false);
      }
    }
    loadIncidents();
    return () => { cancelled = true; };
  }, [newBusinessSections.investigations]);

  const selectedInspections = inspectionOptions.filter(r => selectedInspectionIds.includes(String(r.INSPECTION_ID || r.id)));
  const selectedIncidents = incidentOptions.filter(r => selectedIncidentIds.includes(String(r.INCIDENT_ID || r.id)));
  return (
    <div className="new-business-sections">
      {/* Inspection Reports */}
      <div className="business-section-card">
        <label className="business-section-header">
          <input
            type="checkbox"
            checked={newBusinessSections.inspectionReports}
            onChange={(e) => setNewBusinessSections({
              ...newBusinessSections, 
              inspectionReports: e.target.checked
            })}
            className="checkbox-input"
          />
          Inspection Reports
        </label>
        {newBusinessSections.inspectionReports && (
          <div className="business-section-content">
            {inspectionLoading && <p>Loading inspection reports…</p>}
            {inspectionError && <p className="error">{inspectionError}</p>}
            {!inspectionLoading && !inspectionError && (
              <>
                {inspectionOptions.length === 0 ? (
                  <p>No inspection reports available</p>
                ) : (
                  <div
                    className="inspection-checkbox-list"
                    style={{
                      maxHeight: '140px',
                      overflowY: 'auto',
                      border: '1px solid #ddd',
                      borderRadius: 4,
                      padding: '4px 8px'
                    }}
                  >
                    {inspectionOptions.map(r => {
                      const id = String(r.INSPECTION_ID || r.id);
                      const date = r.INSPECTION_DATE || r.inspectionDate;
                      const time = (r.INSPECTION_TIME || r.inspectionTime || '').toString().substring(0,5);
                      const loc = [r.LOCATION_ADDRESS, r.LOCATION_NAME].filter(Boolean).join(' — ');
                      const label = `${date || 'Unknown date'} ${time || ''} • ${r.CATEGORY || ''} • ${loc || ''}`;
                      const checked = selectedInspectionIds.includes(id);
                      return (
                        <label
                          key={id}
                          className="checkbox-row"
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'auto 1fr',
                            alignItems: 'flex-start',
                            columnGap: 8,
                            fontSize: 'inherit',
                            marginBottom: 4
                          }}
                        >
                          <span style={{ display: 'flex', justifyContent: 'flex-start' }}>
                            <input
                              type="checkbox"
                              value={id}
                              checked={checked}
                              onChange={(e) => {
                                let nextIds;
                                if (e.target.checked) {
                                  nextIds = selectedInspectionIds.includes(id)
                                    ? selectedInspectionIds
                                    : [...selectedInspectionIds, id];
                                } else {
                                  nextIds = selectedInspectionIds.filter(x => x !== id);
                                }
                                setSelectedInspectionIds(nextIds);
                                if (typeof setNewBusinessData === 'function') {
                                  const summaries = inspectionOptions
                                    .filter(r => nextIds.includes(String(r.INSPECTION_ID || r.id)))
                                    .map(r => {
                                      const rid = String(r.INSPECTION_ID || r.id);
                                      const rdate = r.INSPECTION_DATE || r.inspectionDate;
                                      const rtime = (r.INSPECTION_TIME || r.inspectionTime || '').toString().substring(0,5);
                                      const rloc = [r.LOCATION_ADDRESS, r.LOCATION_NAME].filter(Boolean).join(' — ');
                                      const rlabel = `${rdate || 'Unknown date'} ${rtime || ''} • ${r.CATEGORY || ''} • ${rloc || ''}`;
                                      return { id: rid, label: rlabel };
                                    });
                                  setNewBusinessData(prev => ({
                                    ...prev,
                                    selectedInspectionIds: nextIds,
                                    selectedInspectionSummaries: summaries
                                  }));
                                }
                              }}
                            />
                          </span>
                          <span>{label}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
                {selectedInspections.length > 0 && (
                  <div className="inspection-preview">
                    {selectedInspections.map(r => {
                      const id = String(r.INSPECTION_ID || r.id);
                      const date = r.INSPECTION_DATE || r.inspectionDate;
                      const time = (r.INSPECTION_TIME || r.inspectionTime || '').toString().substring(0,5);
                      const loc = [r.LOCATION_ADDRESS, r.LOCATION_NAME].filter(Boolean).join('  ');
                      const deficienciesLabel = r.HAS_DEFICIENCIES ? 'Yes' : 'No';
                      // Use the same PDF endpoint as InspectionHistory, but open via a blob: URL
                      // so the new tab renders the PDF correctly.
                      const pdfPath = `/api/inspectionReports/pdf/${encodeURIComponent(id)}`;
                      const pdfUrl = getApiResourceUrl(pdfPath);
                      return (
                        <div key={id} className="inspection-preview-item">
                          <p className="preview-title">{r.CATEGORY || 'Inspection'}  {date} {time}</p>
                          <p className="preview-detail">Location: {loc || 'N/A'}</p>
                          <p className="preview-detail">
                            Deficiencies: {deficienciesLabel}{' '}
                            <a
                              href={getApiResourceUrl(pdfUrl)}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={async (e) => {
                                // Prefer the InspectionHistory pattern: fetch blob then open blob: URL
                                e.preventDefault();
                                try {
                                  const resp = await API.get(pdfPath, { responseType: 'blob' });
                                  const ct = resp.headers['content-type'] || '';
                                  if (!ct.includes('application/pdf')) {
                                    const text = await resp.data.text?.().catch(() => null);
                                    console.warn('Unexpected content-type for PDF response:', ct, text);
                                    if (resp.status === 440 || resp.status === 401) {
                                      window.location.href = getAppPath('/login');
                                      return;
                                    }
                                    alert('Unable to open PDF. Please try again.');
                                    return;
                                  }
                                  const blobUrl = window.URL.createObjectURL(
                                    new Blob([resp.data], { type: 'application/pdf' })
                                  );
                                  const w = window.open(blobUrl, '_blank', 'noopener,noreferrer');
                                  if (!w) {
                                    alert('Popup blocked. Allow popups for this site to view the PDF in a new tab.');
                                  }
                                } catch (err) {
                                  console.error('Failed to fetch inspection report PDF:', err);
                                  const status = err?.response?.status;
                                  if (status === 440 || status === 401) {
                                    window.location.href = getAppPath('/login');
                                    return;
                                  }
                                  alert('Failed to fetch PDF.');
                                }
                              }}
                            >
                              View PDF
                            </a>
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* Fallback for read-only mode: show saved summaries with links */}
                {selectedInspections.length === 0 && newBusinessData?.selectedInspectionSummaries?.length > 0 && (
                  <div className="inspection-preview">
                    {newBusinessData.selectedInspectionSummaries.map(summary => {
                      const pdfPath = `/api/inspectionReports/pdf/${encodeURIComponent(summary.id)}`;
                      const pdfUrl = getApiResourceUrl(pdfPath);
                      return (
                        <div key={summary.id} className="inspection-preview-item">
                          <p className="preview-title">{summary.label}</p>
                          <p className="preview-detail">
                            <a
                              href={getApiResourceUrl(pdfUrl)}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={async (e) => {
                                e.preventDefault();
                                try {
                                  const resp = await API.get(pdfPath, { responseType: 'blob' });
                                  const ct = resp.headers['content-type'] || '';
                                  if (!ct.includes('application/pdf')) {
                                    if (resp.status === 440 || resp.status === 401) {
                                      window.location.href = getAppPath('/login');
                                      return;
                                    }
                                    alert('Unable to open PDF. Please try again.');
                                    return;
                                  }
                                  const blobUrl = window.URL.createObjectURL(
                                    new Blob([resp.data], { type: 'application/pdf' })
                                  );
                                  window.open(blobUrl, '_blank', 'noopener,noreferrer');
                                } catch (err) {
                                  console.error('Failed to fetch inspection report PDF:', err);
                                  const status = err?.response?.status;
                                  if (status === 440 || status === 401) {
                                    window.location.href = getAppPath('/login');
                                    return;
                                  }
                                  alert('Failed to fetch PDF.');
                                }
                              }}
                            >
                              View Report PDF
                            </a>
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
            <div className="radio-group">
              <label className="radio-label">
                <input type="radio" name="inspectionFollow" value="no" defaultChecked />
                <span>No Follow-up</span>
              </label>
              <label className="radio-label">
                <input type="radio" name="inspectionFollow" value="yes" />
                <span>Follow-up Required</span>
              </label>
            </div>
            <button
              onClick={() => {
                // If there is at least one selected inspection, associate the
                // corrective action with the first one (most common case). This
                // gives us an inspectionId and building/location context to send
                // to the backend when creating corrective actions from minutes.
                const first = selectedInspections[0];
                const inspId = first ? String(first.INSPECTION_ID || first.id) : null;
                const building = first
                  ? [first.LOCATION_ADDRESS, first.LOCATION_NAME].filter(Boolean).join(' — ')
                  : '';
                const category = first?.CATEGORY || '';
                const inspectionDate = first?.INSPECTION_DATE || '';
                const inspectionTime = first?.INSPECTION_TIME || '';
                addCorrectiveAction('inspection', { 
                  inspectionId: inspId, 
                  building,
                  category,
                  inspectionDate,
                  inspectionTime
                });
              }}
              className="btn-add-action"
            >
              <Plus className="btn-icon" />
              Add Corrective Action
            </button>
          </div>
        )}
      </div>

      {/* Incident and Investigation Reports */}
      <div className="business-section-card">
        <label className="business-section-header">
          <input
            type="checkbox"
            checked={newBusinessSections.investigations}
            onChange={(e) => setNewBusinessSections({
              ...newBusinessSections, 
              investigations: e.target.checked
            })}
            className="checkbox-input"
          />
          Incident and Investigation Reports
        </label>
        {newBusinessSections.investigations && (
          <div className="business-section-content">
            {incidentLoading && <p>Loading incident reports…</p>}
            {incidentError && <p className="error">{incidentError}</p>}
            {!incidentLoading && !incidentError && (
              <>
                {incidentOptions.length === 0 ? (
                  <p>No incident or investigation reports available</p>
                ) : (
                  <div
                    className="inspection-checkbox-list"
                    style={{
                      maxHeight: '140px',
                      overflowY: 'auto',
                      border: '1px solid #ddd',
                      borderRadius: 4,
                      padding: '4px 8px'
                    }}
                  >
                    {incidentOptions.map(r => {
                      const id = String(r.INCIDENT_ID || r.id);
                      const date = r.DATE || r.date;
                      const time = (r.TIME || r.time || '').toString().substring(0,5);
                      const dept = r.PRIMARY_DEPARTMENT || r.primaryDepartment;
                      const label = `${date || 'Unknown date'} ${time || ''} • ${r.TITLE || 'Incident'} • ${dept || ''}`;
                      const checked = selectedIncidentIds.includes(id);
                      return (
                        <label
                          key={id}
                          className="checkbox-row"
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'auto 1fr',
                            alignItems: 'flex-start',
                            columnGap: 8,
                            fontSize: 'inherit',
                            marginBottom: 4
                          }}
                        >
                          <span style={{ display: 'flex', justifyContent: 'flex-start' }}>
                            <input
                              type="checkbox"
                              value={id}
                              checked={checked}
                              onChange={async (e) => {
                                if (e.target.checked) {
                                  const nextIds = selectedIncidentIds.includes(id)
                                    ? selectedIncidentIds
                                    : [...selectedIncidentIds, id];
                                  setSelectedIncidentIds(nextIds);
                                  // Fetch full details for this incident if we don't already have them
                                  if (!selectedIncidentDetails[id]) {
                                    try {
                                      const detailRes = await API.get(`/api/incidents/${id}`);
                                      const detail = detailRes.data && (detailRes.data.incident || detailRes.data) ? (detailRes.data.incident || detailRes.data) : null;
                                      if (detail) {
                                        setSelectedIncidentDetails(prev => ({ ...prev, [id]: detail }));
                                      }
                                    } catch (fetchErr) {
                                      console.error('Failed to load incident detail for', id, fetchErr);
                                      setSelectedIncidentDetails(prev => ({ ...prev, [id]: { _error: fetchErr.message || 'Failed to load details' } }));
                                    }
                                  }
                                  if (typeof setNewBusinessData === 'function') {
                                    const summaries = incidentOptions
                                      .filter(r => nextIds.includes(String(r.INCIDENT_ID || r.id)))
                                      .map(r => {
                                        const rid = String(r.INCIDENT_ID || r.id);
                                        const rdate = r.DATE || r.date;
                                        const rtime = (r.TIME || r.time || '').toString().substring(0,5);
                                        const dept = r.PRIMARY_DEPARTMENT || r.primaryDepartment;
                                        const title = r.TITLE || 'Incident';
                                        const rlabel = `${rdate || 'Unknown date'} ${rtime || ''} • ${title}${dept ? ` • ${dept}` : ''}`;
                                        return { id: rid, label: rlabel };
                                      });
                                    setNewBusinessData(prev => ({
                                      ...prev,
                                      selectedIncidentIds: nextIds,
                                      selectedIncidentSummaries: summaries
                                    }));
                                  }
                                } else {
                                  setSelectedIncidentIds(prev => prev.filter(x => x !== id));
                                  if (typeof setNewBusinessData === 'function') {
                                    const nextIds = selectedIncidentIds.filter(x => x !== id);
                                    const summaries = incidentOptions
                                      .filter(r => nextIds.includes(String(r.INCIDENT_ID || r.id)))
                                      .map(r => {
                                        const rid = String(r.INCIDENT_ID || r.id);
                                        const rdate = r.DATE || r.date;
                                        const rtime = (r.TIME || r.time || '').toString().substring(0,5);
                                        const dept = r.PRIMARY_DEPARTMENT || r.primaryDepartment;
                                        const title = r.TITLE || 'Incident';
                                        const rlabel = `${rdate || 'Unknown date'} ${rtime || ''} • ${title}${dept ? ` • ${dept}` : ''}`;
                                        return { id: rid, label: rlabel };
                                      });
                                    setNewBusinessData(prev => ({
                                      ...prev,
                                      selectedIncidentIds: nextIds,
                                      selectedIncidentSummaries: summaries
                                    }));
                                  }
                                  // Optionally keep cached details in case user reselects; do not delete
                                }
                              }}
                            />
                          </span>
                          <span>{label}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
                {selectedIncidents.length > 0 && (
                  <div className="inspection-preview">
                    {selectedIncidents.map(r => {
                      const id = String(r.INCIDENT_ID || r.id);
                      const cached = selectedIncidentDetails[id];
                      const detail = cached || r;
                      const date = detail.DATE || detail.date || r.DATE || r.date;
                      const time = (detail.TIME || detail.time || r.TIME || r.time || '').toString().substring(0,5);
                      const dept = detail.PRIMARY_DEPARTMENT || detail.primaryDepartment || r.PRIMARY_DEPARTMENT || r.primaryDepartment || '';
                      const title = detail.TITLE || r.TITLE || 'Incident';

                      // Build a compact label and a link to the full incident/investigation view.
                      const label = `${date || 'Unknown date'} ${time || ''} - ${title}${dept ? ` - ${dept}` : ''}`;
                      // Fire Department incidents open in the Fire investigations dashboard.
                      const isFireDept = String(dept || '').trim().toLowerCase() === 'fire department' || String(dept || '').toLowerCase().includes('fire');
                      const incidentUrl = getAppPath(`${isFireDept ? '/fire-incident-investigations' : '/incident-investigations'}?incidentId=${encodeURIComponent(id)}`);

                      return (
                        <p key={id} className="preview-detail">
                          <a
                            href={incidentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {label}
                          </a>
                        </p>
                      );
                    })}
                  </div>
                )}
                {/* Fallback for read-only mode: show saved incident summaries with links */}
                {selectedIncidents.length === 0 && newBusinessData?.selectedIncidentSummaries?.length > 0 && (
                  <div className="inspection-preview">
                    {newBusinessData.selectedIncidentSummaries.map(summary => {
                      // Summary label includes the department (e.g. "... • Fire Department"); use that when present.
                      const isFireDept = /fire\s*department/i.test(String(summary.label || '')) || /^fire$/i.test(String(summary.label || '').trim());
                      const incidentUrl = getAppPath(`${isFireDept ? '/fire-incident-investigations' : '/incident-investigations'}?incidentId=${encodeURIComponent(summary.id)}`);
                      return (
                        <p key={summary.id} className="preview-detail">
                          <a
                            href={incidentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {summary.label}
                          </a>
                        </p>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Worker Health & Safety Concerns */}
      <div className="business-section-card">
        <label className="business-section-header">
          <input
            type="checkbox"
            checked={newBusinessSections.workerConcerns}
            onChange={(e) => setNewBusinessSections({
              ...newBusinessSections, 
              workerConcerns: e.target.checked
            })}
            className="checkbox-input"
          />
          Worker Health & Safety Concerns/Issues Reported
        </label>
        {newBusinessSections.workerConcerns && (
          <textarea
            placeholder="Describe concerns or issues..."
            className="form-textarea"
            rows="3"
            value={newBusinessData?.workerConcernsText || ''}
            onChange={e =>
              typeof setNewBusinessData === 'function' &&
              setNewBusinessData(prev => ({
                ...prev,
                workerConcernsText: e.target.value
              }))
            }
          />
        )}
      </div>

      {/* Training Topic Reviewed */}
      <div className="business-section-card">
        <label className="business-section-header">
          <input
            type="checkbox"
            checked={newBusinessSections.training}
            onChange={(e) => setNewBusinessSections({
              ...newBusinessSections, 
              training: e.target.checked
            })}
            className="checkbox-input"
          />
          Training Topic Reviewed
        </label>
        {newBusinessSections.training && (
          <textarea
            placeholder="Describe training topics reviewed..."
            className="form-textarea"
            rows="3"
            value={newBusinessData?.trainingText || ''}
            onChange={e =>
              typeof setNewBusinessData === 'function' &&
              setNewBusinessData(prev => ({
                ...prev,
                trainingText: e.target.value
              }))
            }
          />
        )}
      </div>

      {/* Procedures, Policies, or Practices */}
      <div className="business-section-card">
        <label className="business-section-header">
          <input
            type="checkbox"
            checked={newBusinessSections.procedures}
            onChange={(e) => setNewBusinessSections({
              ...newBusinessSections, 
              procedures: e.target.checked
            })}
            className="checkbox-input"
          />
          Procedures, Policies, or Practices Reviewed
        </label>
        {newBusinessSections.procedures && (
          <textarea
            placeholder="Describe procedures, policies, or practices reviewed..."
            className="form-textarea"
            rows="3"
            value={newBusinessData?.proceduresText || ''}
            onChange={e =>
              typeof setNewBusinessData === 'function' &&
              setNewBusinessData(prev => ({
                ...prev,
                proceduresText: e.target.value
              }))
            }
          />
        )}
      </div>

      {/* Committee Feedback */}
      <div className="business-section-card">
        <label className="business-section-header">
          <input
            type="checkbox"
            checked={newBusinessSections.feedback}
            onChange={(e) => setNewBusinessSections({
              ...newBusinessSections, 
              feedback: e.target.checked
            })}
            className="checkbox-input"
          />
          Committee Feedback
        </label>
        {newBusinessSections.feedback && (
          <div className="business-section-content">
            <textarea
              placeholder="Enter committee feedback and notes..."
              className="form-textarea"
              rows="4"
              value={newBusinessData?.feedbackText || ''}
              onChange={e =>
                typeof setNewBusinessData === 'function' &&
                setNewBusinessData(prev => ({
                  ...prev,
                  feedbackText: e.target.value
                }))
              }
            />
          </div>
        )}
      </div>

      {/* Add Attachments */}
      <div className="business-section-card">
        <label className="business-section-header">
          <input
            type="checkbox"
            checked={newBusinessSections.attachments}
            onChange={(e) => setNewBusinessSections({
              ...newBusinessSections, 
              attachments: e.target.checked
            })}
            className="checkbox-input"
          />
          Add Attachments
        </label>
        {newBusinessSections.attachments && (
          <div className="business-section-content">
            <p className="attachment-note">
              Upload a signed roster or supporting document (PDF only). This file will be appended to the final minutes PDF sent to committee members.
            </p>
            {attachmentError && <p className="error">{attachmentError}</p>}
            <input
              type="file"
              accept="application/pdf,.pdf"
              className="form-input"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setAttachmentError(null);
                
                if (file.type !== 'application/pdf') {
                  setAttachmentError('Only PDF files are allowed.');
                  e.target.value = '';
                  return;
                }
                
                setAttachmentUploading(true);
                try {
                  const formData = new FormData();
                  formData.append('file', file);
                  const res = await API.post('/api/committeeMeetings/upload-attachment', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                  });
                  if (res.data && res.data.success) {
                    const newAttachment = {
                      id: res.data.attachmentId,
                      url: res.data.url || res.data.attachmentUrl,
                      fileName: res.data.fileName || file.name
                    };
                    setUploadedAttachments((prev) => {
                      const next = [...prev, newAttachment];
                      if (typeof onAttachmentsChange === 'function') {
                        onAttachmentsChange(next);
                      }
                      return next;
                    });
                  } else {
                    setAttachmentError((res.data && (res.data.error || res.data.message)) || 'Failed to upload attachment.');
                  }
                } catch (uploadErr) {
                  console.error('Failed to upload minutes attachment', uploadErr);
                  const status = uploadErr?.response?.status;
                  if (status === 440 || status === 401) {
                    window.location.href = getAppPath('/login');
                    return;
                  }
                  setAttachmentError('Failed to upload attachment. Please try again.');
                } finally {
                  setAttachmentUploading(false);
                  // Clear the file input so the same file can be re-selected if needed
                  e.target.value = '';
                }
              }}
            />
            {attachmentUploading && <p>Uploading attachment…</p>}
            {uploadedAttachments.length > 0 && (
              <div className="attachment-list">
                <p className="attachment-note">Attached PDFs (will be appended to final minutes):</p>
                <ul>
                  {uploadedAttachments.map((a) => (
                    <li key={a.id}>
                      <a
                        href={a.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {a.fileName}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NewBusinessSection;